import { useState } from 'react';
import {
  Building2,
  Calendar,
  Download,
  RefreshCw,
  Truck,
  Wallet,
} from 'lucide-react';
import { BILLING_ACCOUNTS } from './mockComplianceData';

function ActionToast({ message, onClose }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-logi-accent/30 bg-white px-4 py-3 shadow-2xl shadow-blue-500/10 ring-1 ring-logi-accent/30">
      <p className="text-sm text-logi-accent">{message}</p>
      <button
        type="button"
        onClick={onClose}
        className="text-xs text-logi-text0 hover:text-logi-text"
      >
        ✕
      </button>
    </div>
  );
}

function AccountCard({ account, onDownload, onRenew }) {
  const isRenewalSoon =
    new Date(account.contractEnd) - new Date() < 45 * 24 * 60 * 60 * 1000;

  return (
    <div className="group rounded-2xl border border-logi-grid bg-white p-5 transition-all duration-300 hover:border-logi-grid hover:bg-white hover:shadow-lg hover:shadow-black/20">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 ring-1 ring-amber-500/25">
            <Building2 className="h-5 w-5 text-amber-700" strokeWidth={1.75} />
          </div>
          <div>
            <p className="font-bold text-white">{account.companyName}</p>
            <p className="text-xs text-logi-text0">{account.shortCode} · {account.id}</p>
          </div>
        </div>
        <span className="rounded-lg bg-logi-accent/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-logi-accent ring-1 ring-violet-500/25">
          {account.plan}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-logi-grid/80 bg-white px-3 py-2.5">
          <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            <Truck className="h-3 w-3" />
            Active Fleet
          </p>
          <p className="mt-1 text-lg font-bold text-logi-text">{account.fleetSize}</p>
        </div>
        <div className="rounded-xl border border-logi-grid/80 bg-white px-3 py-2.5">
          <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            <Wallet className="h-3 w-3" />
            Monthly
          </p>
          <p className="mt-1 font-mono text-lg font-bold text-emerald-700">
            {account.monthlyAed.toLocaleString()}
            <span className="text-xs text-logi-text0"> AED</span>
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-logi-muted">
        <Calendar className="h-3.5 w-3.5 text-logi-text0" />
        Next invoice:{' '}
        <span className="font-semibold text-logi-text">{account.nextInvoiceDate}</span>
        {isRenewalSoon && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
            Contract ending soon
          </span>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => onDownload(account)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-logi-grid bg-logi-silver/80 px-3 py-2.5 text-xs font-semibold text-logi-text transition hover:border-logi-accent/40 hover:bg-logi-accent/10 hover:text-logi-accent"
        >
          <Download className="h-3.5 w-3.5" />
          Download Invoice
        </button>
        <button
          type="button"
          onClick={() => onRenew(account)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-500 px-3 py-2.5 text-xs font-bold text-white shadow-lg shadow-amber-500/20 transition hover:from-amber-500 hover:to-orange-400"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Renew Contract
        </button>
      </div>
    </div>
  );
}

export default function BillingContractLedger({ accounts = BILLING_ACCOUNTS }) {
  const [toast, setToast] = useState(null);

  const totalMrr = accounts.reduce((sum, a) => sum + a.monthlyAed, 0);
  const totalFleet = accounts.reduce((sum, a) => sum + a.fleetSize, 0);

  const handleDownload = (account) => {
    setToast(`Invoice for ${account.shortCode} queued for download (UI preview).`);
  };

  const handleRenew = (account) => {
    setToast(`Contract renewal initiated for ${account.companyName}.`);
  };

  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-logi-grid bg-white backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-violet-500/20">
              <Wallet className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-logi-text">B2B SaaS CRM Billing &amp; Contract Ledger</h2>
              <p className="text-xs text-logi-text0">
                {accounts.length} active accounts · {totalFleet} licensed trucks
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-emerald-50 px-3 py-2 ring-1 ring-emerald-500/20">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Total MRR
            </p>
            <p className="font-mono text-lg font-bold text-emerald-700">
              {totalMrr.toLocaleString()} <span className="text-xs">AED</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          {accounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              onDownload={handleDownload}
              onRenew={handleRenew}
            />
          ))}
        </div>
      </section>

      {toast && <ActionToast message={toast} onClose={() => setToast(null)} />}
    </>
  );
}
